/**
 * Build a single L-shaped corner cabinet at the shared corner of two
 * perpendicular walls. Pure factory.
 *
 * The L:
 *   - 36" along wallA from the corner (24" deep into the room)
 *   - 36" along wallB from the corner (24" deep into the room)
 *   - meets in a 24"×24" corner box in the room corner
 *
 * Collision uses a 36"×36" axis-aligned bounding box (over-claims the
 * 12"×12" inner-L region — empty in practice in our pipeline).
 * Rendering draws the actual L; see drawObjectShape 'corner-cabinet'.
 *
 * Exemption from front clearance per spec — `customFrontClearance: 0`.
 *
 * Convention: wallA must be the wall whose `bottomLeft` is at the
 * corner. cornerDetection guarantees this.
 */

const {
  INCHES_TO_METERS,
  KITCHEN_CABINET_DIMENSIONS,
  CORNER_CABINET_FOOTPRINT,
  UPPER_CABINET_DIMENSIONS,
} = require('./constants');
const { getWallAxes } = require('./wallMath');

const CORNER_BOX = 24 * INCHES_TO_METERS;
const UPPER_CORNER_BOX = UPPER_CABINET_DIMENSIONS.depth; // 12"

/**
 * @param {Object} args
 * @param {Object} args.wallA - Wall whose `bottomLeft` is at the corner.
 *   The object's rotation matches wallA's inward-facing rotation.
 * @param {Object} args.wallB - The perpendicular wall.
 * @param {Object} args.cornerPoint - { x, y?, z } world point at the corner.
 * @param {number} args.floorY
 * @param {Object} [args.opts] - Variant overrides.
 * @param {number} [args.opts.height] - Cabinet vertical extent.
 * @param {number} [args.opts.cornerBox] - Corner-overlap box size (also
 *   the leg's perpendicular depth).
 * @param {number} [args.opts.alongA] - Leg length along wallA. Defaults
 *   to CORNER_CABINET_FOOTPRINT (36"). Used to shrink one leg when an
 *   opening sits within ~36" of the corner — the L's footprint stops
 *   `OPENING_BUFFER` short of the opening rather than being suppressed
 *   entirely.
 * @param {number} [args.opts.alongB] - Leg length along wallB. Same.
 * @param {number} [args.opts.bottomY] - Y of the cabinet's bottom face
 *   in world coords (defaults to `floorY` for the lower variant).
 * @param {string} [args.opts.label]
 * @param {string} [args.opts.idPrefix]
 * @param {string} [args.opts.fixtureGroupPrefix]
 * @param {boolean} [args.opts.isUpper]
 * @returns {Object} The corner-cabinet object (single).
 */
function buildCornerCabinet({ wallA, wallB, cornerPoint, floorY, opts = {} }) {
  const axesA = getWallAxes(wallA);
  if (!axesA) return null;
  const ry = axesA.wallRotationY;
  const aUnit = axesA.wallDir;
  const normalA = axesA.wallNormal;

  const cabinetHeight = opts.height ?? KITCHEN_CABINET_DIMENSIONS.height;
  const cornerBox = opts.cornerBox ?? CORNER_BOX;
  const alongA = opts.alongA ?? CORNER_CABINET_FOOTPRINT;
  const alongB = opts.alongB ?? CORNER_CABINET_FOOTPRINT;
  const bottomY = opts.bottomY ?? floorY;
  const label = opts.label ?? 'Corner Cabinet';
  const idPrefix = opts.idPrefix ?? 'corner_cabinet';
  const fixtureGroupPrefix = opts.fixtureGroupPrefix ?? 'kitchenCornerCabinet';
  const isUpper = opts.isUpper === true;

  // Bounding-box center: from the corner point, walk halfA along
  // wallA and halfB inward. For perpendicular walls, wallB's
  // along-direction-from-corner equals wallA's inward normal, so
  // wallB's portion of the L sits in the same rectangle.
  const halfA = alongA / 2;
  const halfB = alongB / 2;
  const center = {
    x: cornerPoint.x + halfA * aUnit.x + halfB * normalA.x,
    y: bottomY + cabinetHeight / 2,
    z: cornerPoint.z + halfA * aUnit.z + halfB * normalA.z,
  };

  const stamp = `${wallA.id}_${wallB.id}`;

  return {
    id: `${idPrefix}_${stamp}`,
    type: 'corner-cabinet',
    label,
    category: isUpper
      ? { 'corner-cabinet': true, 'upper-cabinet': true }
      : { 'corner-cabinet': true, 'lower-cabinet': true },
    center,
    dimensions: {
      // Bounding box in object-local frame: x = along wallA (= along-
      // wall axis), z = perpendicular to wallA (= depth into room,
      // which equals along wallB from the corner). Asymmetric L is
      // allowed — the renderer carves the inner missing region based
      // on `cornerBox`, with each wall's leg trimmed independently.
      x: alongA,
      y: cabinetHeight,
      z: alongB,
      // L-shape metadata read by drawObjectShape and the 3D renderer:
      alongA,
      alongB,
      cornerBox,
      isUpper,
    },
    rotation: { x: 0, y: ry, z: 0 },
    parentWallIds: [wallA.id, wallB.id],
    fixtureGroupId: `${fixtureGroupPrefix}_${stamp}`,
    customFrontClearance: 0,
    addedBy: 'kitchen-layout',
    lastModified: new Date().toISOString(),
  };
}

module.exports = {
  buildCornerCabinet,
  CORNER_BOX,
  UPPER_CORNER_BOX,
};
